import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "@/lib/apiBase";
import {
  fetchSocialMediaBlobUrl,
  isSocialAuthMediaUrl,
  resolveMediaUrl,
} from "@/lib/socialMedia";
import { cn } from "@/lib/utils";

/**
 * Single image/GIF attached to a comment (org or Social).
 * Org images and Circa-hosted Social uploads require an authenticated fetch;
 * Giphy GIFs use public CDN urls.
 */
export default function CommentMedia({
  item,
  isSocial = false,
  accessToken,
  orgId,
  className,
}) {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(false);
  const isGif = item?.type === "gif";
  const socialNeedsAuth =
    isSocial && !isGif && isSocialAuthMediaUrl(item?.url);
  const needsAuthFetch = (!isSocial && !isGif) || socialNeedsAuth;

  useEffect(() => {
    if (!item?.url) return undefined;
    setError(false);
    if (isGif) {
      setSrc(item.mp4_url || item.url);
      return undefined;
    }
    if (isSocial && !socialNeedsAuth) {
      setSrc(resolveMediaUrl(item.url));
      return undefined;
    }
    if (!accessToken) return undefined;
    let cancelled = false;
    let objectUrl = null;
    const fetchPromise = socialNeedsAuth
      ? fetchSocialMediaBlobUrl(accessToken, item.url)
      : axios
          .get(`${API_BASE_URL}${item.url}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: { org_id: orgId },
            responseType: "blob",
          })
          .then((res) => URL.createObjectURL(res.data));
    fetchPromise
      .then((object) => {
        if (cancelled) {
          URL.revokeObjectURL(object);
          return;
        }
        objectUrl = object;
        setSrc(object);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [
    item?.url,
    item?.mp4_url,
    isGif,
    isSocial,
    socialNeedsAuth,
    accessToken,
    orgId,
  ]);

  if (!item?.url || error) return null;

  return (
    <div className={cn("mt-1.5 max-w-[240px]", className)}>
      {!src && needsAuthFetch ? (
        <div className="h-32 w-32 rounded-md bg-muted/40 animate-pulse" />
      ) : isGif && item.mp4_url ? (
        <video
          src={src}
          autoPlay
          loop
          muted
          playsInline
          className="max-h-52 w-auto rounded-md object-contain"
        />
      ) : src ? (
        <img
          src={src}
          alt={item.title || "Attached"}
          className="max-h-52 w-auto rounded-md object-contain border border-border"
          loading="lazy"
        />
      ) : null}
    </div>
  );
}
